import {
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { PiWarningCircleBold } from 'react-icons/pi';
import { IoMdAdd } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ReactPaginate from 'react-paginate';
import { IoSearchOutline } from 'react-icons/io5';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getPlans, addPlan, updatePlan, deletePlan } from '../../../redux/actions/Plan.action';

const PlanList = () => {
    const dispatch = useDispatch();
    const plans = useSelector((state) => state.plan);
    const [show, setShow] = useState(false);
    const [updateModal, setUpdateModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const totalCount = plans?.getPlans?.data?.data?.length || 0;


    const handleAddModalClose = () => setShow(false);
    const handleAddModalOpen = () => setShow(true);

    const handleUpdateModalClose = () => {
        setUpdateModal(false);
        setSelectedPlan(null);
    };

    const handleUpdateModalOpen = (plan) => {
        setSelectedPlan(plan);
        setUpdateModal(true);
    };

    const handleDeleteModalClose = () => {
        setDeleteModal(false);
        setSelectedPlan(null);
    };

    const handleDeleteModalOpen = (plan) => {
        setSelectedPlan(plan);
        setDeleteModal(true);
    };

    useEffect(() => {
        const params = {
            page: currentPage,
            limit: rowsPerPage,
            search: searchQuery
        };
        dispatch(getPlans(params));
    }, [dispatch, currentPage, rowsPerPage, searchQuery]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (selected) => {
        setCurrentPage(selected.selected + 1);
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    return (
        <>
            <div className="mt-[100px] d-flex justify-content-end">
                <Button
                    onClick={handleAddModalOpen}
                    className="!bg-theme-dark fw-bold d-flex align-item-center border-0"
                >
                    <IoMdAdd className="mt-1 gap-1" />
                    Add Plan
                </Button>
            </div>

            <div className="mt-[50px] mb-4">
                <div className='mb-3 flex items-center justify-between mt-5'>
                    <div className='d-flex align-items-center gap-2'>
                        <h3 className='fw-bold mb-0'>Plan List</h3>
                    </div>
                    <div className='d-flex align-items-center gap-4'>
                        <span className='bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm fw-bold'>
                            Total : <span className="text-theme-dark">{totalCount}</span>
                        </span>
                        <div className='text-gray-800 fw-bold'>
                            Rows per page:
                        </div>
                        <select
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                            className="px-3 py-1 border rounded-md focus:outline-none focus:border-theme-dark fw-bold text-gray-800"
                        >
                            <option value={10}>10</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>

                <TableContainer>
                    <Table variant="striped" colorScheme="blackAlpha">
                        <Thead>
                            <Tr>
                                <Th>Plan Name</Th>
                                <Th>Duration</Th>
                                <Th>Price</Th>
                                <Th>Create By</Th>
                                <Th className="text-end">Action</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {plans?.getPlans?.data?.data && plans.getPlans.data.data.length > 0 ? (
                                plans.getPlans.data.data.map((plan) => (
                                    <Tr key={plan._id}>
                                        <Td>{plan.planName}</Td>
                                        <Td>{plan.duration} {plan.duration === 1 ? 'Month' : 'Months'}</Td>
                                        <Td>₹{plan.price}</Td>
                                        <Td>{plan.plan_created_by?.name}</Td>
                                        <Td>
                                            <div className="d-flex gap-3 justify-end">
                                                <FaEdit
                                                    onClick={() => handleUpdateModalOpen(plan)}
                                                    className="cursor-pointer border-green-color fs-5 text-success"
                                                    title="update"
                                                />
                                                <MdDelete
                                                    onClick={() => handleDeleteModalOpen(plan)}
                                                    className="cursor-pointer border-green-color fs-5 text-danger"
                                                    title="delete"
                                                />
                                            </div>
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={4} textAlign='center'>No Plans Found</Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </div>
            {/* Add Modal */}
            <Modal show={show} onHide={handleAddModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Plan</Modal.Title>
                </Modal.Header>
                <Formik
                    initialValues={{
                        planName: '',
                        duration: '',
                        price: ''
                    }}
                    validationSchema={Yup.object().shape({
                        planName: Yup.string().required('Plan name is required'),
                        duration: Yup.number().required('Duration is required').min(1, 'Duration must be at least 1 month'),
                        price: Yup.number().required('Price is required').min(0, 'Price must be positive')
                    })}
                    onSubmit={(values, { resetForm }) => {
                        dispatch(addPlan(values));
                        resetForm();
                        handleAddModalClose();
                    }}
                >
                    {({ handleSubmit, values, handleChange, errors, touched }) => (
                        <Form onSubmit={handleSubmit}>
                            <Modal.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Plan Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="planName"
                                        value={values.planName}
                                        onChange={handleChange}
                                        isInvalid={touched.planName && errors.planName}
                                        className="shadow-none focus:!border-theme-dark"
                                    />
                                    <ErrorMessage name="planName" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Duration (months)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="duration"
                                        value={values.duration}
                                        onChange={handleChange}
                                        isInvalid={touched.duration && errors.duration}
                                        className="shadow-none focus:!border-theme-dark"
                                    />
                                    <ErrorMessage name="duration" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="price"
                                        value={values.price}
                                        onChange={handleChange}
                                        isInvalid={touched.price && errors.price}
                                        className="shadow-none focus:!border-theme-dark"
                                    />
                                    <ErrorMessage name="price" component="div" className="text-danger" />
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleAddModalClose}>
                                    Close
                                </Button>
                                <Button type="submit" className="!bg-theme-dark border-0">
                                    Add Plan
                                </Button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>

            {/* Update Modal */}
            <Modal show={updateModal} onHide={handleUpdateModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Update Plan</Modal.Title>
                </Modal.Header>
                <Formik
                    initialValues={{
                        planName: selectedPlan?.planName || '',
                        duration: selectedPlan?.duration || '',
                        price: selectedPlan?.price || ''
                    }}
                    validationSchema={Yup.object().shape({
                        planName: Yup.string().required('Plan name is required'),
                        duration: Yup.number().required('Duration is required').min(1, 'Duration must be at least 1 month'),
                        price: Yup.number().required('Price is required').min(0, 'Price must be positive')
                    })}
                    onSubmit={(values) => {
                        dispatch(updatePlan(selectedPlan._id, values));
                        handleUpdateModalClose();
                    }}
                    enableReinitialize
                >
                    {({ handleSubmit, values, handleChange, errors, touched }) => (
                        <Form onSubmit={handleSubmit}>
                            <Modal.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Plan Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="planName"
                                        value={values.planName}
                                        onChange={handleChange}
                                        isInvalid={touched.planName && errors.planName}
                                        className="shadow-none focus:!border-theme-dark"
                                    />
                                    <ErrorMessage name="planName" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Duration (months)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="duration"
                                        value={values.duration}
                                        onChange={handleChange}
                                        isInvalid={touched.duration && errors.duration}
                                        className="shadow-none focus:!border-theme-dark"
                                    />
                                    <ErrorMessage name="duration" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="price"
                                        value={values.price}
                                        onChange={handleChange}
                                        isInvalid={touched.price && errors.price}
                                        className="shadow-none focus:!border-theme-dark"
                                    />
                                    <ErrorMessage name="price" component="div" className="text-danger" />
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleUpdateModalClose}>
                                    Close
                                </Button>
                                <Button type="submit" className="!bg-theme-dark border-0">
                                    Update Plan
                                </Button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>

            {/* Delete Modal */}
            <Modal show={deleteModal} onHide={handleDeleteModalClose}>
                <Modal.Body className='p-5'>
                    <div>
                        <PiWarningCircleBold className='text-7xl w-100 text-danger' />
                    </div>
                    <div className='text-center mt-4'>
                        <h3>Are You Sure?</h3>
                        <p className='text-md'>Do you really want to delete this plan?</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleDeleteModalClose}
                        className="px-4 bg-transparent border-[1px] text-black border-green-color"
                    >
                        No
                    </Button>
                    <Button
                        type="button"
                        className="bg-danger border-0 px-4"
                        onClick={() => {
                            if (selectedPlan && selectedPlan._id) {
                                dispatch(deletePlan(selectedPlan._id));
                                handleDeleteModalClose();
                            }
                        }}
                    >
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default PlanList;